import { useCallback, useEffect, useRef, useState } from 'react'
import { DEFAULT_OTP_EXPIRY_SECONDS } from '../constants/otp'
import type { UseOtpCountdownResult } from '../types'

function formatMmSs(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const minutes = Math.floor(safe / 60)
  const seconds = safe % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

/**
 * Wall-clock countdown so tab throttling does not drift the expiry display.
 * Interval only drives re-renders; remaining time is derived from `endsAt`.
 */
export function useOtpCountdown(
  active: boolean,
  initialSeconds: number = DEFAULT_OTP_EXPIRY_SECONDS,
): UseOtpCountdownResult {
  const endsAtRef = useRef<number>(0)
  const [remainingSeconds, setRemainingSeconds] = useState(0)

  const syncRemaining = useCallback(() => {
    const left = Math.max(0, Math.ceil((endsAtRef.current - Date.now()) / 1000))
    setRemainingSeconds(left)
    return left
  }, [])

  const restart = useCallback(
    (seconds: number = initialSeconds) => {
      endsAtRef.current = Date.now() + Math.max(0, seconds) * 1000
      syncRemaining()
    },
    [initialSeconds, syncRemaining],
  )

  useEffect(() => {
    if (!active) {
      endsAtRef.current = 0
      setRemainingSeconds(0)
      return
    }

    restart(initialSeconds)

    const id = window.setInterval(() => {
      if (syncRemaining() <= 0) {
        window.clearInterval(id)
      }
    }, 250)

    return () => window.clearInterval(id)
  }, [active, initialSeconds, restart, syncRemaining])

  return {
    remainingSeconds,
    formatted: formatMmSs(remainingSeconds),
    expired: active && remainingSeconds <= 0,
    restart,
  }
}
